/**
 * Cliente HTTP do painel.
 *
 * Caminho relativo (`/api/...`), nunca URL absoluta com host: em
 * desenvolvimento o Vite faz proxy para a API local, em produção o Traefik
 * roteia o mesmo caminho. Assim não existe variável de ambiente de build
 * apontando para o backend — e, principalmente, nenhuma credencial chega ao
 * frontend (§37). A autenticação é o cookie httpOnly, que o navegador envia
 * sozinho e o JavaScript não consegue ler.
 */

import type {
  Activity,
  Campaign,
  CampaignListItem,
  CampaignRun,
  CollectionPlan,
  Diagnosis,
  Discovery,
  DiscoveryInput,
  DiscoveryMath,
  DiscoveryResponse,
  FollowUpItem,
  Proposal,
  OutreachAvailability,
  OutreachKind,
  OutreachMessage,
  WhatsAppOverview,
  WhatsAppStatus,
  Enrichment,
  Presence,
  ImportResult,
  ManualLeadResult,
  Product,
  Prospect,
  ProspectList,
  ProspectNote,
  ProspectScore,
  ProspectSource,
  ProspectStage,
  ProductAnalysis,
  ProductInput,
  ProductUsage,
  ProductMeta,
  TeamMember,
  MemberStats,
  MemberActivity,
  QualifyResult,
  LeadBatch,
  MaxChars,
  DraftQueueItem,
  ReplyInboxItem,
  QueueSummary,
  ProspectBrief,
  AiSpend,
} from './types.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * A mensagem para a tela. Erro de validação da API vem como "Dados inválidos."
 * mais a lista de campos: juntar os dois diz O QUE corrigir.
 */
export function errorText(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const details = Array.isArray(error.details) ? (error.details as { field?: string; message?: string }[]) : [];
  const fields = details.filter((item) => item.message).map((item) => (item.field ? `${item.field}: ${item.message}` : item.message));
  return fields.length > 0 ? fields.join(' · ') : error.message;
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  details?: unknown;
}

export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const hasBody = options.body !== undefined;

  const response = await fetch(`/api${path}`, {
    method: options.method ?? (hasBody ? 'POST' : 'GET'),
    headers: hasBody ? { 'content-type': 'application/json' } : {},
    body: hasBody ? JSON.stringify(options.body) : undefined,
    // Sem isto o cookie de sessão não acompanha a requisição e toda chamada
    // autenticada volta 401.
    credentials: 'same-origin',
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const body = (typeof payload === 'object' && payload !== null ? payload : {}) as ApiErrorBody;
    // Sessão expirada no meio do uso: avisa a casca, que leva ao login
    // guardando a rota. O login e o /auth/me tratam o próprio 401.
    if (response.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent('prospector:unauthorized'));
    }
    throw new ApiError(
      response.status,
      body.error ?? 'http_error',
      body.message ?? `Erro ${response.status}.`,
      body.details,
    );
  }

  return payload as T;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string;
  workspaceId: string;
  activeProfileId: string | null;
}

/** Perfil de empresa (Fase 11) — catálogo, campanhas e prospects próprios. */
export interface CompanyProfile {
  id: string;
  workspace_id: string;
  name: string;
  signer_name: string;
  /** Convite da abordagem. `null` = o texto padrão. */
  outreach_invite?: string | null;
  /** Instância da Evolution. `null` = a do servidor. */
  whatsapp_instance?: string | null;
}

export const auth = {
  me: () => api<{ user: SessionUser; profiles: CompanyProfile[] }>('/auth/me'),
  login: (email: string, password: string) =>
    api<{ user: SessionUser }>('/auth/login', { body: { email, password } }),
  logout: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
  switchProfile: (profileId: string) =>
    api<{ ok: true; activeProfileId: string }>('/auth/profile', { method: 'POST', body: { profileId } }),
  changePassword: (current: string, next: string) =>
    api<{ ok: true; otherSessionsRevoked: number }>('/auth/password', { body: { current, next } }),
};

export const profiles = {
  list: () => api<{ profiles: CompanyProfile[] }>('/profiles'),
  create: (input: { name: string; signerName: string; outreachInvite?: string | null; whatsappInstance?: string | null }) =>
    api<{ profile: CompanyProfile }>('/profiles', { method: 'POST', body: input }),
  update: (id: string, patch: Record<string, unknown>) =>
    api<{ profile: CompanyProfile }>(`/profiles/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => api<{ ok: true }>(`/profiles/${id}`, { method: 'DELETE' }),
};

export const products = {
  list: (includeInactive = false) =>
    api<{ products: Product[] }>(`/products${includeInactive ? '?includeInactive=true' : ''}`),
  get: (id: string) => api<{ product: Product }>(`/products/${id}`),
  /** Sem id: a API gera a partir do nome. */
  create: (input: ProductInput) => api<{ product: Product }>('/products', { method: 'POST', body: input }),
  update: (id: string, patch: Partial<ProductInput>) =>
    api<{ product: Product }>(`/products/${id}`, { method: 'PATCH', body: patch }),
  deactivate: (id: string) => api<{ product: Product }>(`/products/${id}`, { method: 'DELETE' }),
  /** Apaga de vez — a API recusa (409) produto que já foi usado. */
  removePermanently: (id: string) => api<{ deleted: true }>(`/products/${id}?permanent=true`, { method: 'DELETE' }),
  usage: (id: string) => api<{ usage: ProductUsage }>(`/products/${id}/usage`),
  meta: () => api<ProductMeta>('/products/meta'),
  /** Leva o catálogo mudado às notas das empresas do perfil. */
  recalculate: () => api<{ refreshed: number; failed: number }>('/products/recalculate', { method: 'POST', body: {} }),
};

export const team = {
  list: (days?: number) => api<{ members: TeamMember[] }>(`/team${days ? `?days=${days}` : ''}`),
  create: (input: { name: string; email: string; password: string }) =>
    api<{ member: TeamMember }>('/team', { method: 'POST', body: input }),
  rename: (id: string, name: string) => api<{ member: TeamMember }>(`/team/${id}`, { method: 'PATCH', body: { name } }),
  resetPassword: (id: string, password: string) =>
    api<{ ok: true; sessionsRevoked: number }>(`/team/${id}/password`, { method: 'POST', body: { password } }),
  deactivate: (id: string) => api<{ member: TeamMember }>(`/team/${id}/deactivate`, { method: 'POST', body: {} }),
  reactivate: (id: string) => api<{ member: TeamMember }>(`/team/${id}/reactivate`, { method: 'POST', body: {} }),
  activity: (id: string, params: { days?: number; limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) search.set(key, String(value));
    const query = search.toString();
    return api<{ member: TeamMember; stats: MemberStats | null; activities: MemberActivity[]; total: number }>(
      `/team/${id}/activity${query ? `?${query}` : ''}`,
    );
  },
};

export const campaigns = {
  list: () => api<{ campaigns: CampaignListItem[] }>('/campaigns'),
  get: (id: string) => api<{ campaign: Campaign; runs: CampaignRun[] }>(`/campaigns/${id}`),
  create: (input: Record<string, unknown>) =>
    api<{ campaign: Campaign }>('/campaigns', { body: input }),
  update: (id: string, patch: Record<string, unknown>) =>
    api<{ campaign: Campaign }>(`/campaigns/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => api<{ ok: true }>(`/campaigns/${id}`, { method: 'DELETE' }),
  qualify: (id: string) =>
    api<{ jobId: string | null; queued: boolean }>(`/campaigns/${id}/qualify`, {
      method: 'POST',
      body: {},
    }),
  /** Gasto do mês com o Apify e se a coleta está ligada. */
  costs: () =>
    api<{ spentThisMonthUsd: number; monthlyBudgetUsd: number; remainingUsd: number; enabled: boolean }>(
      '/campaigns/costs/monthly',
    ),
  /** Quanto a coleta custaria e se pode rodar. Não chama o Apify. */
  preflight: (id: string) => api<{ plan: CollectionPlan }>(`/campaigns/${id}/collect/preflight`),
  /** Enfileira a coleta paga no Google Maps. */
  collect: (id: string) =>
    api<{ jobId: string | null; queued: boolean; plan: CollectionPlan }>(`/campaigns/${id}/collect`, {
      method: 'POST',
      body: {},
    }),
  /** Quantos leads a campanha tem em cada etapa — antes de excluir. */
  leadBreakdown: (id: string) =>
    api<{ byStage: Record<ProspectStage, number>; restorable: LeadBatch | null }>(`/campaigns/${id}/leads/breakdown`),
  deleteLeads: (id: string, stages: ProspectStage[]) =>
    api<{ batchId: string; deleted: number; byStage: Partial<Record<ProspectStage, number>>; jobsCancelled: number }>(
      `/campaigns/${id}/leads/delete`,
      { body: { stages } },
    ),
  restoreLeads: (id: string, batchId: string) =>
    api<{ restored: number; skipped: number }>(`/campaigns/${id}/leads/restore`, { body: { batchId } }),
};

export interface ProspectQuery {
  status?: string;
  stage?: ProspectStage;
  city?: string;
  niche?: string;
  campaignId?: string;
  priority?: string;
  minScore?: number;
  search?: string;
  sort?: 'score' | 'recent' | 'name' | 'contacted' | 'replied';
  limit?: number;
  offset?: number;
}

/**
 * Monta a query string ignorando o que está vazio.
 *
 * Mandar `status=` vazio faria o Zod da API recusar a listagem inteira com 422
 * — o filtro em branco tem que sumir da URL, não virar valor vazio.
 */
function toQueryString(params: ProspectQuery): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const prospects = {
  list: (params: ProspectQuery = {}) => api<ProspectList>(`/prospects${toQueryString(params)}`),
  summary: (campaignId?: string) =>
    api<{ byStatus: { status: string; count: number }[]; byStage: Record<ProspectStage, number> }>(
      `/prospects/summary${campaignId ? `?campaignId=${campaignId}` : ''}`,
    ),
  get: (id: string) =>
    api<{
      prospect: Prospect;
      sources: ProspectSource[];
      activities: Activity[];
      scores: ProspectScore[];
      enrichment: Enrichment[];
      notes: ProspectNote[];
      diagnosis: Diagnosis;
      analyses: ProductAnalysis[];
      presence: Presence;
    }>(`/prospects/${id}`),
  /**
   * Cadastro manual: links + o que o vendedor sabe. O worker lê o site,
   * qualifica e, se a empresa passar, encadeia a análise da IA.
   */
  createManual: (input: Record<string, unknown>) =>
    api<ManualLeadResult>('/prospects/manual', { body: input }),
  addNote: (id: string, body: string) =>
    api<{ note: ProspectNote; reanalyzeSuggested: boolean }>(`/prospects/${id}/notes`, { body: { body } }),
  /** Enfileira a análise com IA; a API recusa na hora quem não pode ir para ela. */
  analyze: (id: string, productId?: string) =>
    api<{ jobId: string | null; queued: boolean }>(`/prospects/${id}/analyze`, {
      body: productId ? { productId } : {},
    }),
  /** Pontua na hora, sem visitar o site (isso é trabalho do worker). */
  qualify: (id: string, productId?: string) =>
    api<{ result: QualifyResult }>(`/prospects/${id}/qualify`, {
      body: productId ? { productId } : {},
    }),
  /** Enfileira a leitura do site e a pontuação. */
  /** Relê site, velocidade e CNPJ; com `instagram`, lê também o perfil (pago). */
  enrich: (id: string, options: { instagram?: boolean } = {}) =>
    api<{ jobId: string | null; queued: boolean }>(`/prospects/${id}/enrich`, {
      method: 'POST',
      body: options.instagram ? { instagram: true } : {},
    }),
  update: (id: string, patch: Record<string, unknown>) =>
    api<{ prospect: Prospect }>(`/prospects/${id}`, { method: 'PATCH', body: patch }),
  remove: (id: string) => api<{ ok: true }>(`/prospects/${id}`, { method: 'DELETE' }),
  /** Marca (ou remarca) a próxima retomada de contato. */
  setFollowUp: (id: string, date: string, note?: string) =>
    api<{ followUp: { follow_up_at: string; follow_up_note: string | null } }>(`/prospects/${id}/follow-up`, {
      method: 'PUT',
      body: { date, ...(note ? { note } : {}) },
    }),
  clearFollowUp: (id: string) => api<{ ok: true }>(`/prospects/${id}/follow-up`, { method: 'DELETE' }),
  /** Retomadas de hoje e atrasadas; `ahead` inclui os próximos dias. */
  followUps: (ahead = 0) => api<{ followUps: FollowUpItem[] }>(`/follow-ups?ahead=${ahead}`),
  discovery: (id: string) => api<DiscoveryResponse>(`/prospects/${id}/discovery`),
  saveDiscovery: (id: string, input: DiscoveryInput) =>
    api<{ discovery: Discovery; math: DiscoveryMath }>(`/prospects/${id}/discovery`, { method: 'PUT', body: input }),
  proposal: (id: string) => api<Proposal>(`/prospects/${id}/proposal`),
  /** O corpo é o texto do CSV: a API não recebe multipart (ver rota de import). */
  import: (campaignId: string, content: string, dryRun: boolean) =>
    api<ImportResult>(`/campaigns/${campaignId}/import`, { body: { content, dryRun } }),
};

/** Mensagens de abordagem (Fase 9): a IA redige, a pessoa decide. */
export const messages = {
  list: (prospectId: string) =>
    api<{
      messages: OutreachMessage[];
      generating: boolean;
      lastError: string | null;
      available: OutreachAvailability;
      maxChars?: MaxChars;
    }>(`/prospects/${prospectId}/messages`),
  generate: (prospectId: string, kind: OutreachKind = 'first', instructions?: string) =>
    api<{ jobId: string | null; queued: boolean }>(`/prospects/${prospectId}/messages/generate`, {
      body: { kind, ...(instructions ? { instructions } : {}) },
    }),
  /** A pessoa escreve do zero, sem IA. Sem `kind`, o sistema decide pela conversa. */
  write: (prospectId: string, body: string, kind?: OutreachKind) =>
    api<{ messageId: string; version: number; kind: OutreachKind; lint: string[] }>(`/prospects/${prospectId}/messages/manual`, {
      body: { body, ...(kind ? { kind } : {}) },
    }),
  edit: (id: string, body: string) =>
    api<{ message: OutreachMessage; lint: string[] }>(`/messages/${id}`, { method: 'PATCH', body: { body } }),
  /** A régua da IA sobre o texto em edição, sem gravar (decisão 45). */
  lint: (id: string, body: string) => api<{ warnings: string[] }>(`/messages/${id}/lint`, { body: { body } }),
  /**
   * Aprovar. Com aviso da régua e sem `acknowledgeWarnings`, a API devolve 409
   * com `details.warnings` — e a tela pergunta antes de aprovar por cima.
   */
  approve: (id: string, acknowledgeWarnings = false) =>
    api<{ message: OutreachMessage; warnings: string[] }>(`/messages/${id}/approve`, {
      method: 'POST',
      body: acknowledgeWarnings ? { acknowledgeWarnings } : {},
    }),
  reject: (id: string, reason: string) =>
    api<{ message: OutreachMessage }>(`/messages/${id}/reject`, { method: 'POST', body: { reason } }),
  /** Pede o envio pelo WhatsApp. O worker envia, respeitando teto, intervalo e horário. */
  send: (id: string, confirmRetry = false) =>
    api<{ queued: boolean; jobId: string | null }>(`/messages/${id}/send`, { body: confirmRetry ? { confirmRetry } : {} }),
};

/** Fila de aprovação e caixa de respostas (decisão 47). */
export const queue = {
  summary: () => api<QueueSummary>('/queue/summary'),
  drafts: (offset = 0) => api<{ items: DraftQueueItem[]; total: number }>(`/queue/drafts?offset=${offset}`),
  replies: (offset = 0) => api<{ items: ReplyInboxItem[]; total: number }>(`/queue/replies?offset=${offset}`),
  brief: (prospectId: string) => api<ProspectBrief>(`/prospects/${prospectId}/brief`),
  aiSpend: () => api<AiSpend>('/admin/ai-spend'),
};

/** Os avisos da régua que vieram num 409 de aprovação, ou `undefined` se o erro é outro. */
export function approvalWarnings(error: unknown): string[] | undefined {
  if (!(error instanceof ApiError) || error.status !== 409) return undefined;
  const details = error.details as { needsConfirmation?: boolean; warnings?: unknown } | undefined;
  return details?.needsConfirmation && Array.isArray(details.warnings) ? (details.warnings as string[]) : undefined;
}

/** WhatsApp (Fase 10): conversa, lista de não contatar e estado da instância. */
export const whatsapp = {
  overview: (prospectId: string) => api<WhatsAppOverview>(`/prospects/${prospectId}/whatsapp`),
  suppress: (prospectId: string, reason: string) =>
    api<{ added: boolean }>(`/prospects/${prospectId}/whatsapp/suppression`, { body: { reason } }),
  unsuppress: (prospectId: string) =>
    api<{ removed: number }>(`/prospects/${prospectId}/whatsapp/suppression`, { method: 'DELETE' }),
  status: () => api<WhatsAppStatus>('/whatsapp/status'),
};
